import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { format, parseISO } from "date-fns"
import { fr } from "date-fns/locale"
import { AgentEvaluation, Call, SalesStage, CustomerExperienceStage, CallStage } from "@/types"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export { formatAgentName } from "./formatAgentName";

export function formatDate(dateString: string, formatStr: string = "dd MMM yyyy"): string {
  return format(parseISO(dateString), formatStr, { locale: fr })
}

export function formatTime(dateString: string): string {
  return format(parseISO(dateString), "HH:mm", { locale: fr })
}

export function formatDateTime(dateString: string): string {
  return format(parseISO(dateString), "dd MMM yyyy 'à' HH:mm", { locale: fr })
}

export function formatDuration(seconds: number): string {
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
}

export function getAgentEvaluations(agentId: string, evaluations: AgentEvaluation[]): AgentEvaluation[] {
  return evaluations.filter(evaluation => evaluation.agentId === agentId)
}

export function getAgentCalls(agentId: string, calls: Call[]): Call[] {
  return calls.filter(call => call.agentId === agentId)
}

export function getCallsByStage(calls: Call[], stage: SalesStage): Call[] {
  return calls.filter(call => call.salesStage === stage)
}

export function salesStageValue(stage: SalesStage, allStages: SalesStage[]): number {
  return allStages.indexOf(stage);
}

export function calculateConversionRate(stage: SalesStage, calls: Call[], allStages: SalesStage[]): number {
  const totalCalls = calls.length;
  if (totalCalls === 0) return 0;

  const callsReachingStage = calls.filter(call =>
    salesStageValue(call.salesStage, allStages) >= salesStageValue(stage, allStages)
  ).length;

  return Math.round((callsReachingStage / totalCalls) * 100);
}

export function getStageColor(stage: CallStage): string {
  const salesColors: Record<SalesStage, string> = {
    'Introduction': 'bg-blue-100 text-blue-800',
    'Qualification': 'bg-indigo-100 text-indigo-800',
    'Présentation': 'bg-purple-100 text-purple-800',
    'Objection': 'bg-orange-100 text-orange-800',
    'Négociation': 'bg-yellow-100 text-yellow-800',
    'Conclusion': 'bg-green-100 text-green-800'
  }

  const customerExperienceColors: Record<CustomerExperienceStage, string> = {
    'Accueil': 'bg-blue-100 text-blue-800',
    'Écoute': 'bg-indigo-100 text-indigo-800',
    'Compréhension': 'bg-purple-100 text-purple-800',
    'Solution': 'bg-orange-100 text-orange-800',
    'Résolution': 'bg-yellow-100 text-yellow-800',
    'Conclusion': 'bg-green-100 text-green-800'
  }

  return (salesColors[stage as SalesStage] || customerExperienceColors[stage as CustomerExperienceStage]) || 'bg-gray-100 text-gray-800'
}

export function getPerformanceColor(score: number): string {
  if (score >= 85) return 'text-success'
  if (score >= 70) return 'text-warning'
  return 'text-destructive'
}

export function getPerformanceClass(score: number): string {
  if (score >= 85) return 'performance-indicator high'
  if (score >= 70) return 'performance-indicator medium'
  return 'performance-indicator low'
}

export function truncateText(text: string, maxLength: number = 150): string {
  if (text.length <= maxLength) return text
  return `${text.substring(0, maxLength)}...`
}
